import { useLocation } from "react-router-dom";
import { REFINANCING_LIMITS } from "shared/CalculatorLimits";
import { useRefinancingRatesQuery } from "web_myplann/Api/Calculators/useRefinancingRatesQuery";
import { CalculatorRatesNote } from "web_myplann/Calculators/Components/CalculatorRatesNote";
import { CalculatorShell } from "web_myplann/Calculators/Components/CalculatorShell";
import { RefinancingPropertyPriceNotice } from "web_myplann/Calculators/Components/RefinancingPropertyPriceNotice";
import { RefinancingResultSection } from "web_myplann/Calculators/Components/RefinancingResultSection";
import { getRefinancingFormSchema } from "web_myplann/Calculators/Forms/refinancingFormSchema";
import { useCalculatorForm } from "web_myplann/Calculators/Hooks/useCalculatorForm";
import { useRefinancingLead } from "web_myplann/Calculators/Hooks/useRefinancingLead";
import { useYearsLabel } from "web_myplann/Calculators/Hooks/useYearsLabel";
import { CalculatorType } from "web_myplann/Calculators/Types/CalculatorType";
import { getCalculatorPrefill } from "web_myplann/Calculators/Utils/getCalculatorPrefill";
import { getMaxPaidYears } from "web_myplann/Calculators/Utils/getMaxPaidYears";
import { getRefinancingPlan } from "web_myplann/Calculators/Utils/getRefinancingPlan";
import { hasRefinancingInputs } from "web_myplann/Calculators/Utils/hasRefinancingInputs";
import { mapRefinancingLeadRequest } from "web_myplann/Calculators/Utils/mapRefinancingLeadRequest";
import { mapRefinancingPrefillToForm } from "web_myplann/Calculators/Utils/mapRefinancingPrefillToForm";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getCurrencySymbol } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { SliderFormRow } from "web_myplann/Shared/Components/SliderFormRow";
import { CurrencyCode } from "web_myplann/Shared/constants";

export const RefinancingCalculatorPage = () => {
	const { t } = useResource();
	const yearsLabel = useYearsLabel();
	const prefill = getCalculatorPrefill(useLocation().state);
	const {
		data: rates = [],
		isPending: isRatesPending,
		isError: isRatesError,
	} = useRefinancingRatesQuery();
	const { send, ...lead } = useRefinancingLead(prefill?.contractId);
	const { control, watch, setValue, handleSubmit } = useCalculatorForm(
		getRefinancingFormSchema,
		mapRefinancingPrefillToForm(prefill),
	);

	const values = watch();
	const plan = getRefinancingPlan({ ...values, rates });
	const { rate } = plan;
	const maxPaidYears = getMaxPaidYears(values.years);
	const hasRates = !isRatesError && rates.length > 0;
	const hasInputs = hasRefinancingInputs(values);
	const hasResult =
		hasInputs && plan.newMonthlyPayment > 0 && rate !== undefined;
	const isResultLoading = hasInputs && isRatesPending;
	const isPropertyPriceMissing =
		!!prefill &&
		(values.propertyPrice ?? 0) < REFINANCING_LIMITS.propertyPriceMin;

	const changeYears = (years: number) =>
		setValue("paidYears", Math.min(values.paidYears, getMaxPaidYears(years)));

	const onSubmit = handleSubmit((submitted) => {
		if (rate !== undefined) {
			send(mapRefinancingLeadRequest(submitted, { ...plan, rate }, prefill));
		}
	});

	return (
		<CalculatorShell
			type={CalculatorType.Refinancing}
			onSubmit={onSubmit}
			notice={isPropertyPriceMissing && <RefinancingPropertyPriceNotice />}
			fields={
				<>
					<FlatRowFormInput
						control={control}
						name="propertyPrice"
						label={t(Resources.Calculators.Refinancing.PropertyPrice)}
						placeholder={t(Resources.Calculators.FillPlaceholder)}
						type="number"
						max={REFINANCING_LIMITS.propertyPriceMax}
						suffix={getCurrencySymbol(CurrencyCode.CZK)}
					/>
					<FlatRowFormInput
						control={control}
						name="monthlyPayment"
						label={t(Resources.Calculators.Refinancing.MonthlyPayment)}
						placeholder={t(Resources.Calculators.FillPlaceholder)}
						type="number"
						max={REFINANCING_LIMITS.monthlyPaymentMax}
						suffix={getCurrencySymbol(CurrencyCode.CZK)}
					/>
					<FlatRowFormInput
						control={control}
						name="interest"
						label={t(Resources.Calculators.Refinancing.Interest)}
						placeholder={t(Resources.Calculators.FillPlaceholder)}
						type="float"
						suffix="%"
					/>
					<SliderFormRow
						control={control}
						name="years"
						label={t(Resources.Calculators.Refinancing.Years)}
						formatValue={yearsLabel}
						onValueChange={changeYears}
						min={REFINANCING_LIMITS.yearsMin}
						max={REFINANCING_LIMITS.yearsMax}
					/>
					<SliderFormRow
						control={control}
						name="paidYears"
						label={t(Resources.Calculators.Refinancing.PaidPeriod)}
						formatValue={yearsLabel}
						min={REFINANCING_LIMITS.paidPeriodMin}
						max={maxPaidYears}
						isLast
					/>
				</>
			}
			isResultLoading={isResultLoading}
			result={
				hasResult &&
				rate !== undefined && (
					<RefinancingResultSection
						plan={plan}
						rate={rate}
						clientRate={values.interest}
						lead={lead}
					/>
				)
			}
		>
			{hasInputs && !isRatesPending && (
				<CalculatorRatesNote
					hasRates={hasRates}
					isOfferMissing={!hasResult && plan.leftToPay > 0}
				/>
			)}
		</CalculatorShell>
	);
};
